import MenuButton from "@mui/joy/MenuButton";
import Menu from "@mui/joy/Menu";
import MenuItem from "@mui/joy/MenuItem";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Dropdown from "@mui/joy/Dropdown";
import {
  Avatar,
  ListItemDecorator,
  Stack,
  Typography,
  Box,
  useColorScheme,
} from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import { useUserMeQuery } from "../__generated__/types-and-hooks";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useTranslation } from "../i18n";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export interface TeamMiniCardProps {
  name: string;
  slug: string;
  picture?: string | null;
}

function profilePlaceholder(mode: string | undefined) {
  return mode === "light"
    ? "/images/qcsku_light.jpg"
    : "/images/qcsku_dark.jpg";
}

export function TeamMiniCard({ name, slug, picture }: TeamMiniCardProps) {
  const { mode } = useColorScheme();
  const avatar = picture || profilePlaceholder(mode);

  return (
    <>
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            m: "-2px",
            borderRadius: 4,
            background:
              "linear-gradient(45deg, #007cf0 0%, #00b3f0 25%, #00c3ff 50%, #0098ff 75%, #005eff 100%)",
          },
        }}
      >
        <Avatar
          size="sm"
          sx={{
            height: 32,
            width: "100%",
            minWidth: 32,
            borderRadius: 4,
          }}
          src={avatar}
        />
      </Box>
      <Typography
        noWrap
        sx={{
          display: { xs: "none", sm: "none", lg: "flex" },
          textAlign: "left",
          ml: 1,
          fontWeight: "lg",
          flex: 1,
        }}
        endDecorator={<ArrowDropDownIcon fontSize="medium" />}
      >
        {name}
      </Typography>
    </>
  );
}

export default function TeamSelect() {
  const user = useUserMeQuery();
  const navigate = useNavigate();
  const { teamSlug } = useParams();
  const t = useTranslation();
  const { mode } = useColorScheme();

  let team = null;

  const teamEdges = user.data?.user_me.teamEdges ?? [];
  if (teamEdges.length === 1) {
    team = teamEdges[0].team;
  } else if (user.data && teamSlug) {
    const edge = teamEdges.find(({ team }) => team.slug === teamSlug);
    if (edge) {
      team = edge.team;
    }
  }

  // if (!team) return null;

  return (
    <Dropdown>
      <MenuButton
        fullWidth
        sx={{
          p: 0,
          m: 0,
          maxHeight: "32px",
        }}
        color={team ? "neutral" : "primary"}
        startDecorator={
          team ? null : <ArrowForwardIcon sx={{ ml: 1 }} fontSize="medium" />
        }
      >
        {team ? (
          <TeamMiniCard
            key={team.id}
            name={team.name}
            slug={team.slug}
            picture={team.picture}
          />
        ) : (
          <Typography
            sx={{ display: { xs: "none", sm: "none", lg: "flex" } }}
            endDecorator={<ArrowDropDownIcon fontSize="medium" />}
          >
            {t("Switch Team")}
          </Typography>
        )}
      </MenuButton>
      <Menu>
        {user.loading && <MenuItem>Loading...</MenuItem>}
        {(user.data?.user_me.teamEdges || []).map(({ team }) => (
          <MenuItem key={team.id} onClick={() => navigate(`/${team.slug}`)}>
            <ListItemDecorator sx={{ color: "inherit" }}>
              <Box
                sx={{
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    m: "-2px",
                    borderRadius: "50%",
                    background:
                      "linear-gradient(45deg, #007cf0 0%, #00b3f0 25%, #00c3ff 50%, #0098ff 75%, #005eff 100%)",
                  },
                }}
              >
                <Avatar
                  size="sm"
                  sx={{
                    height: 23,
                    width: 23,
                  }}
                  src={team.picture || profilePlaceholder(mode)}
                />
              </Box>
            </ListItemDecorator>
            <Stack direction="column">
              <Typography
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: 120,
                }}
              >
                {team.name}{" "}
                {!team ? (
                  <Typography color="primary" fontSize={12}>
                    <VerifiedIcon />
                  </Typography>
                ) : (
                  ""
                )}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Dropdown>
  );
}
